import React, { useCallback, useEffect, useState } from 'react';
import { FaCalendarAlt, FaExclamationTriangle, FaSearch, FaSlidersH, FaSpinner, FaTimes } from 'react-icons/fa';

import { CabecalhoSecao, Chip, Chips, Conteudo, Controles, Estado, Resumo, Secao } from './styles';
import WidgetRenderer from './WidgetRenderer';
import ModalFiltros from './ModalFiltros';
import Button from '../Button';
import { Campo } from '../FichaCadastroGenerico';

import { IConfigDashboard } from '../../constants/dashboards';
import useDashboard from '../../hooks/useDashboard';
import useFiltrosDashboard, { Filtros } from '../../hooks/useFiltrosDashboard';
import { buscarCamposLayout } from '../../services/layout';
import { PRESETS, PresetPeriodo, periodoDoPreset } from '../../utils/periodo';

// Texto curto dos filtros ativos (fora o período)
function resumoFiltros(dash: IConfigDashboard, filtros: Filtros, campos: Campo[]) {
  const periodo = [dash.periodo.ini, dash.periodo.fim];
  return campos
    .filter(campo => !periodo.includes(campo.campo))
    .map(campo => {
      const valor = filtros[campo.campo];
      if (!valor || valor === '0') return null;
      if (campo.tipinp === 'checkbox') return campo.label;
      if (campo.tipinp === 'select') {
        return `${campo.label}: ${campo.opcoes?.find(opcao => opcao.valor === valor)?.rotulo ?? valor}`;
      }
      return `${campo.label}: ${filtros[`_${campo.campo}`] || valor}`;
    })
    .filter((texto): texto is string => !!texto);
}

// Um dashboard (POST /dashboard?rel=) com atalhos de período, filtros do laytel e os widgets
export default function SecaoDashboard({ dash }: { dash: IConfigDashboard }) {
  const { filtros, salvar } = useFiltrosDashboard(dash);
  const { dados, carregando, erro, recarregar } = useDashboard(dash, filtros);
  const [campos, setCampos] = useState<Campo[]>([]);
  const [modalAberto, setModalAberto] = useState(false);

  useEffect(() => {
    const controle = new AbortController();

    buscarCamposLayout(dash.layout, controle.signal)
      .then(lidos => setCampos(lidos.filter(campo => campo.tipinp !== 'button' && !dash.ocultar?.includes(campo.campo))))
      .catch(error => {
        // sem o layout o dashboard funciona; só o resumo e o modal de filtros ficam vazios
        if (!controle.signal.aborted) console.error(error);
      });

    return () => controle.abort();
  }, [dash]);

  const ini = filtros[dash.periodo.ini];
  const fim = filtros[dash.periodo.fim];
  const presetAtivo = PRESETS.find(p => {
    const periodo = periodoDoPreset(p.id);
    return periodo.ini === ini && periodo.fim === fim;
  })?.id;

  function aplicarPreset(id: PresetPeriodo) {
    const periodo = periodoDoPreset(id);
    salvar({ ...filtros, [dash.periodo.ini]: periodo.ini, [dash.periodo.fim]: periodo.fim });
  }

  // Tira todos os filtros, menos o período
  function limparFiltros() {
    salvar({ [dash.periodo.ini]: ini, [dash.periodo.fim]: fim });
    setModalAberto(false);
  }

  const abrirFiltros = () => setModalAberto(true);
  const fecharFiltros = useCallback(() => setModalAberto(false), []);
  const ativos = resumoFiltros(dash, filtros, campos);

  function conteudo() {
    if (carregando && !dados) {
      return (
        <Estado role='status'>
          <FaSpinner className='icone girando' />
          Calculando o dashboard...
        </Estado>
      );
    }

    if (erro) {
      return (
        <Estado role='alert'>
          <FaExclamationTriangle className='icone erro' />
          <strong>Não foi possível carregar</strong>
          {erro}
          <Button variante='secundario' tamanho='sm' texto='Tentar de novo' onClick={recarregar} />
        </Estado>
      );
    }

    if (!dados || dados.widgets.length === 0) {
      return (
        <Estado>
          <FaSearch className='icone' />
          <strong>Nenhum dado encontrado</strong>
          Não há movimento para os filtros escolhidos.
          <Button variante='secundario' tamanho='sm' texto='Alterar filtros' onClick={abrirFiltros} />
        </Estado>
      );
    }

    return (
      <Conteudo $atualizando={carregando} aria-busy={carregando}>
        <WidgetRenderer widgets={dados.widgets} />
      </Conteudo>
    );
  }

  return (
    <Secao aria-label={dash.titulo}>
      <CabecalhoSecao>
        <h2>{dash.titulo}</h2>
        <Controles>
          <Chips role='group' aria-label='Período'>
            {PRESETS.map(p => (
              <Chip
                key={p.id}
                type='button'
                $ativo={presetAtivo === p.id}
                aria-pressed={presetAtivo === p.id}
                onClick={() => aplicarPreset(p.id)}
              >
                {p.rotulo}
              </Chip>
            ))}
          </Chips>
          {ativos.length > 0 && (
            <Button variante='fantasma' tamanho='sm' texto='Limpar filtros' icone={<FaTimes />} onClick={limparFiltros} />
          )}
          <Button variante='secundario' tamanho='sm' texto='Filtros' icone={<FaSlidersH />} onClick={abrirFiltros} />
        </Controles>
      </CabecalhoSecao>

      <Resumo type='button' onClick={abrirFiltros} title='Alterar filtros'>
        <span className='periodo'>
          <FaCalendarAlt size={12} />
          {ini} a {fim}
        </span>
        {ativos.length > 0 && <span>{ativos.join(' · ')}</span>}
        {carregando && dados && <span>Atualizando...</span>}
      </Resumo>

      {conteudo()}

      {modalAberto && (
        <ModalFiltros
          titulo={`Filtros - ${dash.titulo}`}
          campos={campos}
          filtros={filtros}
          onAplicar={novos => {
            salvar(novos);
            setModalAberto(false);
          }}
          onLimpar={limparFiltros}
          onClose={fecharFiltros}
        />
      )}
    </Secao>
  );
}
